import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';

import {
  calcComparisonScenario,
  isJointPolicy,
  type ComparisonScenario,
  type ComparisonScenarioInput,
  type InjectedSelfFundingChange,
} from '@shared';
import { FundingPolicyType } from '@shared/appModel';
import { selectClient } from '@client/model/selectors';

import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { useSession } from '@/features/guided-financial-planning/context';
import { withoutPreviouslyFinalizedPolicies } from '@/features/guided-financial-planning/utils/finalized-policies';
import { useAddPolicyFromQuote } from './use-add-policy-from-quote';
import { usePartnerClient } from './use-partner-client';
import { getCarrierName, type GuidedPlanningQuote } from './use-quotes-display';

interface SelectedQuoteInfo {
  quoteId: string;
  quote?: GuidedPlanningQuote;
  ccrcTypeAProduct?: CcrcTypeAGuidedSelection;
  ccrcCouplePolicies?: CcrcTypeACouplePolicies;
  policyFundingSource: PendingPolicyFundingSource;
}

export interface ScenarioMetrics {
  /** Self-funding projected coverage. */
  projectedAssets: number;
  /** Policy coverage (0 for without-insurance scenario). */
  policyCoverage: number;
  /** Combined coverage (self-funding + policy + annuity). */
  totalCoverage: number;
  /** Percentage of LTC cost covered. */
  coveragePercent: number;
  /** Coverage gap: max(0, ltcCost - totalCoverage). */
  coverageGap: number;
  /** Surplus coverage: max(0, totalCoverage - ltcCost). */
  surplus: number;
  /** Sum of existing assets value (self-funding + policy face value). */
  currentAssets: number;
  /** Combined total value of all funding sources at start of LTC. */
  projectedAssetsAtStart: number;
  /** Remaining assets after modeled LTC costs. */
  projectedAssetsAtEnd: number;
}

export interface SelectedPolicyInfo {
  quoteId: string;
  productName: string;
  carrierName: string;
  policyType: FundingPolicyType | null;
  totalCost: number;
}

export interface FundingAllocation {
  selfFundingSourceId: string;
  accountName: string;
  amount: number;
  existingValue?: number;
}

export interface InsuranceBenefitScenario {
  insuranceIsBetter: boolean;
  hasGapReduction: boolean;
  hasGapElimination: boolean;
  hasSurplusImprovement: boolean;
}

export interface PlanComparisonResult {
  projectedLtcCost: number;
  withoutInsurance: ScenarioMetrics;
  withInsurance: ScenarioMetrics;
  selectedPolicy: SelectedPolicyInfo | null;
  fundingAllocations: FundingAllocation[];
  gapReduction: number;
  netBenefit: number;
  benefitScenario: InsuranceBenefitScenario;
  hasPolicy: boolean;
  isCoupleMode: boolean;
  partnerFirstName: string | null;
  isLoading: boolean;
}

const DEFAULT_METRICS: ScenarioMetrics = {
  projectedAssets: 0,
  policyCoverage: 0,
  totalCoverage: 0,
  coveragePercent: 0,
  coverageGap: 0,
  surplus: 0,
  currentAssets: 0,
  projectedAssetsAtStart: 0,
  projectedAssetsAtEnd: 0,
};

const EMPTY_BENEFIT_SCENARIO: InsuranceBenefitScenario = {
  insuranceIsBetter: false,
  hasGapReduction: false,
  hasGapElimination: false,
  hasSurplusImprovement: false,
};

/**
 * Derive the benefit scenario from a snapshot's metrics.
 * Used when returning cached snapshot data for completed sessions.
 */
function deriveBenefitScenarioFromSnapshot(
  snapshot: PlanComparisonSnapshot,
): InsuranceBenefitScenario {
  const withoutGap = snapshot.withoutInsurance.coverageGap;
  const withGap = snapshot.withInsurance.coverageGap;
  const { gapReduction, netBenefit } = snapshot;

  const insuranceIsBetter = gapReduction > 0 || netBenefit > 0;
  const hasGapReduction = withoutGap > 0 && withGap > 0 && withGap < withoutGap;
  const hasGapElimination = withoutGap > 0 && withGap === 0 && insuranceIsBetter;
  const hasSurplusImprovement = withoutGap === 0 && withGap === 0 && insuranceIsBetter;

  return { insuranceIsBetter, hasGapReduction, hasGapElimination, hasSurplusImprovement };
}

/**
 * Map a `ComparisonScenario` from the shared helper into the hook's
 * `ScenarioMetrics` shape (the UI's contract).
 *
 * Coverage-gap / surplus / coveragePercent are recomputed against a
 * canonical `projectedLtcCost`: when the user is exploring a joint
 * policy, the with-insurance scenario activates couple-mode and includes
 * the partner's LTC cost in its projected total. Using the same LTC cost
 * for both scenarios keeps the comparison apples-to-apples — otherwise
 * the without-insurance card would report a smaller (single-mode) cost
 * than the with-insurance card for the same household.
 */
function toScenarioMetrics(
  scenario: ComparisonScenario,
  canonicalLtcCost: number,
): ScenarioMetrics {
  const totalCoverage = scenario.totalCoverage;
  return {
    projectedAssets: scenario.selfFundingCoverage,
    policyCoverage: scenario.policyCoverage,
    totalCoverage,
    coveragePercent:
      canonicalLtcCost > 0 ? Math.round((totalCoverage / canonicalLtcCost) * 100) : 0,
    coverageGap: Math.max(0, canonicalLtcCost - totalCoverage),
    surplus: Math.max(0, totalCoverage - canonicalLtcCost),
    currentAssets: scenario.currentAssets,
    projectedAssetsAtStart: scenario.projectedAssetsAtStart,
    projectedAssetsAtEnd: scenario.projectedAssetsAtEnd,
  };
}

/**
 * Build the `InjectedSelfFundingChange[]` input for the shared helper
 * from the user's allocations toward the selected quote.
 *
 * The full allocated policy amount is deducted at time zero. Existing account
 * withdrawal settings remain unchanged.
 */
export function buildFundingDeductions(
  allocations: Record<string, number>,
): InjectedSelfFundingChange[] {
  return Object.entries(allocations).map(([selfFundingSourceId, amount]) => ({
    selfFundingSourceId,
    lumpSumDeduction: amount > 0 ? amount : undefined,
  }));
}

function calculatePlanComparison(
  client: Client | null,
  partnerClient: Client | null,
  selectedQuoteInfo: SelectedQuoteInfo | null,
  allocations: Record<string, number>,
  userSelectedExistingLtcPolicy: boolean,
): Omit<PlanComparisonResult, 'isLoading'> {
  if (!client) {
    return {
      projectedLtcCost: 0,
      withoutInsurance: DEFAULT_METRICS,
      withInsurance: DEFAULT_METRICS,
      selectedPolicy: null,
      fundingAllocations: [],
      gapReduction: 0,
      netBenefit: 0,
      benefitScenario: EMPTY_BENEFIT_SCENARIO,
      hasPolicy: selectedQuoteInfo !== null,
      isCoupleMode: false,
      partnerFirstName: null,
    };
  }

  // Couple-mode is determined by the selected quote, not by primary's
  // existing policies — when the user is exploring a joint product, both
  // scenarios should aggregate as a couple so the comparison is
  // apples-to-apples. When no joint quote is selected, neither scenario
  // pulls partner contributions in.
  const useCoupleMode = !!(
    selectedQuoteInfo &&
    partnerClient &&
    (selectedQuoteInfo.ccrcCouplePolicies ||
      isJointPolicy(selectedQuoteInfo.policyFundingSource.policyType, partnerClient))
  );

  const baseInput: ComparisonScenarioInput = {
    primary: client,
    partner: useCoupleMode ? partnerClient : null,
    includePrimaryExistingPolicies: userSelectedExistingLtcPolicy,
    isCoupleMode: useCoupleMode,
  };

  const withoutScenario = calcComparisonScenario(baseInput);

  let withScenario = withoutScenario;
  let selectedPolicy: SelectedPolicyInfo | null = null;

  if (selectedQuoteInfo) {
    const { quoteId, quote, ccrcTypeAProduct, ccrcCouplePolicies, policyFundingSource } =
      selectedQuoteInfo;

    // A CCRC couple selection models one membership per partner: the primary's
    // is injected on the primary, the partner's onto a partner clone, so
    // couple-mode aggregates both partners' care and coverage.
    const primaryPolicy = ccrcCouplePolicies?.primary ?? policyFundingSource;
    const primaryFundingSourceId = primaryPolicy.pendingFundingSourceId;

    let scenarioPartner = baseInput.partner;
    let partnerFundingSourceId: string | null = null;

    if (ccrcCouplePolicies && partnerClient) {
      partnerFundingSourceId = ccrcCouplePolicies.partner.pendingFundingSourceId;

      const partnerWithMembership = cloneDeep(partnerClient);
      const partnerFundingSources = partnerWithMembership.multipleFundingSources;

      if (partnerFundingSources) {
        partnerFundingSources.ltcPolicy = {
          ...partnerFundingSources.ltcPolicy,
          [partnerFundingSourceId]: ccrcCouplePolicies.partner,
        };
      }

      scenarioPartner = partnerWithMembership;
    }

    withScenario = calcComparisonScenario({
      ...baseInput,
      partner: scenarioPartner,
      injectedPolicies: [
        { fundingSourceId: primaryFundingSourceId, policyFundingSource: primaryPolicy },
      ],
      injectedSelfFundingChanges: buildFundingDeductions(allocations),
    });

    const calculatedPrimary =
      withScenario._derivedPrimary.multipleFundingSources?.ltcPolicy?.[primaryFundingSourceId];
    let totalCost = calculatedPrimary?.policyTotalCost ?? 0;

    if (partnerFundingSourceId) {
      const calculatedPartner =
        withScenario._derivedPartner?.multipleFundingSources?.ltcPolicy?.[partnerFundingSourceId];
      totalCost += calculatedPartner?.policyTotalCost ?? 0;
    }

    const productName = ccrcTypeAProduct?.productName ?? quote?.productName ?? 'Selected Policy';
    const carrierName = ccrcTypeAProduct?.communityName ?? (quote ? getCarrierName(quote) : '');

    selectedPolicy = {
      quoteId,
      productName,
      carrierName,
      policyType: policyFundingSource.policyType ?? null,
      totalCost,
    };
  }

  // The with-scenario decides couple-mode (its injected joint policy is
  // what triggers it). Use its projectedLtcCost as canonical so the
  // without-scenario reports the same household cost.
  const projectedLtcCost = withScenario.projectedLtcCost;
  const withoutInsurance = toScenarioMetrics(withoutScenario, projectedLtcCost);
  const withInsurance = toScenarioMetrics(withScenario, projectedLtcCost);

  const selfFunding = client.multipleFundingSources?.selfFunding ?? {};
  const fundingAllocations: FundingAllocation[] = Object.entries(allocations)
    .filter(([, amount]) => amount > 0)
    .map(([selfFundingSourceId, amount]) => {
      const source = selfFunding[selfFundingSourceId];
      return {
        selfFundingSourceId,
        accountName: source?.selfFundingName ?? source?.accountType ?? 'Unknown Account',
        amount,
        existingValue: source?.existingAssetsValue ?? 0,
      };
    });

  const gapReduction = withoutInsurance.coverageGap - withInsurance.coverageGap;
  const netBenefit = withInsurance.projectedAssetsAtEnd - withoutInsurance.projectedAssetsAtEnd;
  const insuranceIsBetter = netBenefit > 0;
  const withoutGap = withoutInsurance.coverageGap;
  const withGap = withInsurance.coverageGap;

  return {
    projectedLtcCost,
    withoutInsurance,
    withInsurance,
    selectedPolicy,
    fundingAllocations,
    gapReduction,
    netBenefit,
    benefitScenario: {
      insuranceIsBetter,
      hasGapReduction: withoutGap > 0 && withGap > 0 && withGap < withoutGap,
      hasGapElimination: withoutGap > 0 && withGap === 0 && insuranceIsBetter,
      hasSurplusImprovement: withoutGap === 0 && withGap === 0 && insuranceIsBetter,
    },
    hasPolicy: selectedQuoteInfo !== null,
    isCoupleMode: withScenario.isCoupleMode,
    partnerFirstName: withScenario.partnerFirstName,
  };
}

/**
 * Hook that compares two scenarios for the plan step:
 * 1. Without Insurance - Self-funding only
 * 2. With Insurance - Self-funding (minus premium allocations) + session policy
 *
 * Reads the selected quote from session.stepData.selectedQuotes and injects
 * the policy into calculations without requiring database persistence.
 *
 * For completed sessions, returns the cached snapshot to ensure consistent
 * read-only display after finalization.
 */
export function usePlanComparisonScenarios(): PlanComparisonResult {
  const client = useSelector(selectClient);
  const { session, isLoading } = useSession();
  const { flags } = useFeatureFlags();
  const { selectedQuote: selectedQuoteInfo } = useAddPolicyFromQuote();
  const { partnerClient } = usePartnerClient();

  const snapshot = session?.stepData?.planComparisonSnapshot;

  // A reopened session's earlier-saved policy is the plan's own, not existing coverage.
  const previouslyFinalizedPolicies = session?.stepData?.previouslyFinalizedPolicies;
  const comparisonClient = useMemo(
    () => withoutPreviouslyFinalizedPolicies(client, previouslyFinalizedPolicies),
    [client, previouslyFinalizedPolicies],
  );
  const comparisonPartnerClient = useMemo(
    () => withoutPreviouslyFinalizedPolicies(partnerClient, previouslyFinalizedPolicies),
    [partnerClient, previouslyFinalizedPolicies],
  );

  const userSelectedExistingLtcPolicy = Boolean(
    flags.existingLtcPolicyTypeOnCard &&
    (session?.stepData?.selectedAssets ?? []).includes('existing-ltc-policy'),
  );

  const allocations = useMemo(() => {
    if (!selectedQuoteInfo) return {};
    return (session?.stepData?.policyFundingAllocations?.[selectedQuoteInfo.quoteId] ??
      {}) as Record<string, number>;
  }, [session?.stepData?.policyFundingAllocations, selectedQuoteInfo]);

  // The multipleFundingSourcesSnapshot preserves the original funding sources
  // from before finalization modified them (deductions, policy persistence).
  // Used to recalculate new display metrics for old completed sessions.
  const mfsSnapshot = session?.stepData?.multipleFundingSourcesSnapshot;

  const result = useMemo((): PlanComparisonResult => {
    if (session?.status === 'completed' && snapshot) {
      const snapshotHasNewFields = typeof snapshot.withoutInsurance.currentAssets === 'number';

      let withoutInsurance: ScenarioMetrics;
      let withInsurance: ScenarioMetrics;

      if (snapshotHasNewFields) {
        withoutInsurance = snapshot.withoutInsurance as ScenarioMetrics;
        withInsurance = snapshot.withInsurance as ScenarioMetrics;
      } else if (mfsSnapshot && client) {
        // Old snapshot: recalculate new fields using the original funding sources
        const tempClient = cloneDeep(client);
        tempClient.multipleFundingSources = mfsSnapshot;
        const recalculated = calculatePlanComparison(
          tempClient,
          partnerClient,
          selectedQuoteInfo,
          allocations,
          userSelectedExistingLtcPolicy,
        );

        withoutInsurance = {
          ...DEFAULT_METRICS,
          ...snapshot.withoutInsurance,
          currentAssets: recalculated.withoutInsurance.currentAssets,
          projectedAssetsAtStart: recalculated.withoutInsurance.projectedAssetsAtStart,
          projectedAssetsAtEnd: recalculated.withoutInsurance.projectedAssetsAtEnd,
        };
        withInsurance = {
          ...DEFAULT_METRICS,
          ...snapshot.withInsurance,
          currentAssets: recalculated.withInsurance.currentAssets,
          projectedAssetsAtStart: recalculated.withInsurance.projectedAssetsAtStart,
          projectedAssetsAtEnd: recalculated.withInsurance.projectedAssetsAtEnd,
        };
      } else {
        withoutInsurance = { ...DEFAULT_METRICS, ...snapshot.withoutInsurance };
        withInsurance = { ...DEFAULT_METRICS, ...snapshot.withInsurance };
      }

      // Re-derive couple-mode from the snapshot's policy + currently-fetched
      // partner client. (Snapshot doesn't carry these flags, so we recompute
      // the same way as the live path.)
      const completedSessionIsCoupleMode = !!(
        partnerClient &&
        (selectedQuoteInfo?.ccrcCouplePolicies ||
          (snapshot.selectedPolicy?.policyType &&
            isJointPolicy(snapshot.selectedPolicy.policyType, partnerClient)))
      );

      return {
        projectedLtcCost: snapshot.projectedLtcCost,
        withoutInsurance,
        withInsurance,
        selectedPolicy: snapshot.selectedPolicy,
        fundingAllocations: snapshot.fundingAllocations,
        gapReduction: snapshot.gapReduction,
        netBenefit: snapshot.netBenefit,
        benefitScenario: deriveBenefitScenarioFromSnapshot(snapshot),
        hasPolicy: snapshot.selectedPolicy !== null,
        isCoupleMode: completedSessionIsCoupleMode,
        partnerFirstName: completedSessionIsCoupleMode
          ? (partnerClient?.clientFirstName ?? null)
          : null,
        isLoading: false,
      };
    }

    return {
      ...calculatePlanComparison(
        comparisonClient,
        comparisonPartnerClient,
        selectedQuoteInfo,
        allocations,
        userSelectedExistingLtcPolicy,
      ),
      isLoading,
    };
  }, [
    session?.status,
    snapshot,
    mfsSnapshot,
    session?.stepData?.selectedAssets,
    client,
    partnerClient,
    comparisonClient,
    comparisonPartnerClient,
    selectedQuoteInfo,
    allocations,
    userSelectedExistingLtcPolicy,
    flags.existingLtcPolicyTypeOnCard,
    isLoading,
  ]);

  return result;
}
